import { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  ActionsTd,
  Attention,
  PayTable,
  SectionHeader,
  SubTabs,
  TableState,
  Td,
  Th,
} from '@/components/admin/PaymentsLayout';
import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import { useAdminCreditRequests, useResolveCreditRequest } from '@/hooks/useCreditRequests';

/**
 * Customer credit requests — "apply my credit to these invoices" / "send me a
 * check". Filed from the customer's Payments & credits page, emailed to the
 * Company → Credit requests list. An open request closes itself once
 * QuickBooks shows the credit used; Mark done is for what the mirror cannot
 * see (a check mailed but not yet recorded).
 */

type Status = 'open' | 'done' | 'cancelled' | 'all';

export function CreditRequestsPanel() {
  const [status, setStatus] = useState<Status>('open');
  const q = useAdminCreditRequests(status);
  const openQ = useAdminCreditRequests('open');
  const resolve = useResolveCreditRequest();
  const rows = q.data?.requests ?? [];
  const openCount = openQ.data?.requests.length ?? 0;

  return (
    <div className="space-y-3">
      <SectionHeader
        description={
          <>
            Customers can&apos;t apply credits themselves — they tell us here. Apply it (or mail the
            check) in QuickBooks, then contact the customer. Requests close themselves once
            QuickBooks shows the credit used.
          </>
        }
      />

      <SubTabs
        tabs={[
          { key: 'open', label: 'Open', count: openCount },
          { key: 'done', label: 'Done' },
          { key: 'cancelled', label: 'Cancelled' },
          { key: 'all', label: 'All' },
        ]}
        value={status}
        onChange={setStatus}
        right={
          <Button variant="secondary" size="sm" disabled={q.isFetching} onClick={() => { void q.refetch(); void openQ.refetch(); }}>
            {q.isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </Button>
        }
      />

      {status === 'open' && (
        <Attention count={openCount}>
          customers asked us to apply a credit or send a check. Do it in QuickBooks, then mark it done.
        </Attention>
      )}

      {resolve.isError && (
        <p className="text-sm text-red-700">{resolve.error instanceof Error ? resolve.error.message : 'Failed'}</p>
      )}

      <TableState
        loading={q.isLoading}
        error={q.isError ? q.error : null}
        empty={rows.length === 0}
        emptyText={`Nothing ${status === 'all' ? 'on file' : status} right now.`}
      >
        <PayTable minWidth={900}>
          <thead>
            <tr>
              <Th>Requested</Th>
              <Th>Customer</Th>
              <Th>Request</Th>
              <Th right>Amount</Th>
              <Th>Details</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brix-border">
            {rows.map((r) => {
              const n = (r.invoices ?? []).length;
              const what =
                r.request_type === 'refund_check'
                  ? 'Send a check'
                  : r.apply_oldest
                    ? 'Apply to oldest invoices'
                    : `Apply to ${n} invoice${n === 1 ? '' : 's'}`;
              return (
                <tr key={r.id}>
                  <Td className="whitespace-nowrap text-xs">
                    {fmtMirrorDate(r.created_at.slice(0, 10))}
                    <span className="block text-brix-muted">{r.requested_by_name ?? r.requested_by_email}</span>
                  </Td>
                  <Td>
                    {r.customer_id ? (
                      <Link to={`/admin/customers/${r.customer_id}/billing`} className="font-semibold text-brix-ink hover:underline">
                        {r.customer_name ?? 'Customer'}
                      </Link>
                    ) : (
                      <span className="font-semibold">{r.customer_name ?? 'Customer'}</span>
                    )}
                  </Td>
                  <Td className="text-xs">
                    {what}
                    <span className="block text-brix-muted">
                      from {r.source_kind === 'credit_memo' ? 'credit memo' : 'payment'}
                      {r.source_reference ? ` ${r.source_reference}` : ` #${r.source_id}`}
                    </span>
                  </Td>
                  <Td right mono>
                    {formatUsd(Number(r.requested_amount))}
                    <span className="block text-[11px] text-brix-muted">of {formatUsd(Number(r.credit_at_request))}</span>
                  </Td>
                  <Td className="max-w-[280px] text-xs">
                    {n > 0 && (
                      <span className="block text-brix-ink">
                        {(r.invoices ?? []).map((i) => `${i.doc_number ?? i.qbo_invoice_id} ${formatUsd(i.amount)}`).join(' · ')}
                      </span>
                    )}
                    {r.request_type === 'refund_check' && (
                      <span className="block whitespace-pre-wrap text-brix-ink">Mail to: {r.mail_to || 'billing address on file'}</span>
                    )}
                    {r.notes && <span className="block whitespace-pre-wrap italic text-brix-ink">“{r.notes}”</span>}
                    {r.email_error && <span className="block font-semibold text-red-700">Team email failed: {r.email_error}</span>}
                    {r.status !== 'open' && (
                      <span className="block text-brix-muted">
                        {r.status === 'done' ? 'Done' : 'Cancelled'}
                        {r.resolved_at ? ` ${fmtMirrorDate(r.resolved_at.slice(0, 10))}` : ''}
                        {r.resolved_by ? ` · ${r.resolved_by}` : ''}
                      </span>
                    )}
                  </Td>
                  <ActionsTd>
                    {r.status === 'open' ? (
                      <>
                        <Button
                          size="sm"
                          disabled={resolve.isPending}
                          onClick={() => {
                            const note = window.prompt('Mark done — optional note (e.g. check number):');
                            if (note === null) return;
                            resolve.mutate({ id: r.id, action: 'done', note });
                          }}
                        >
                          Mark done
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={resolve.isPending}
                          onClick={() => {
                            const note = window.prompt('Cancel this request — reason (optional):');
                            if (note === null) return;
                            resolve.mutate({ id: r.id, action: 'cancel', note });
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <span className="text-[11px] text-brix-muted">closed</span>
                    )}
                  </ActionsTd>
                </tr>
              );
            })}
          </tbody>
        </PayTable>
      </TableState>
    </div>
  );
}
